/* ==========================================================================
   My Teacher Says — custom behaviour

   Deferred, dependency-free, and everything here is progressive: with JS disabled the page is
   fully readable and shoppable. Nothing reads or writes layout before first paint.

   All motion is gated on prefers-reduced-motion (BUILDSPEC §5, §10).
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----------------------------------------------------------------------
     Scroll reveal — sections drift up as they enter, slightly staggered.
     Elements start visible in CSS and are only hidden once JS confirms it can
     reveal them again, so a JS failure can never leave content invisible.
     ---------------------------------------------------------------------- */
  function initReveal() {
    if (reduceMotion || !('IntersectionObserver' in window)) return;

    var targets = document.querySelectorAll('[data-mts-reveal]');
    if (!targets.length) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12 }
    );

    targets.forEach(function (el, i) {
      el.classList.add('mts-reveal');
      el.style.transitionDelay = (i % 4) * 90 + 'ms';
      observer.observe(el);
    });
  }

  /* ----------------------------------------------------------------------
     Classroom reels — load and play on hover only.
     preload="none" means nothing is fetched until intent is shown, and touch
     devices never fire mouseenter, so they never download a video at all.
     ---------------------------------------------------------------------- */
  function initReels() {
    document.querySelectorAll('[data-mts-hover-video]').forEach(function (video) {
      var parent = video.closest('.mts-classrooms__reel') || video;

      parent.addEventListener('mouseenter', function () {
        if (reduceMotion) return;
        var playing = video.play();
        if (playing && playing.catch) playing.catch(function () {});
      });

      parent.addEventListener('mouseleave', function () {
        video.pause();
        video.currentTime = 0;
      });
    });

    document.querySelectorAll('[data-mts-reels]').forEach(function (list) {
      var row = list.parentElement;
      var prev = row.querySelector('[data-mts-reel-prev]');
      var next = row.querySelector('[data-mts-reel-next]');

      function scrollBy(direction) {
        var first = list.querySelector('.mts-classrooms__reel');
        var step = first ? first.getBoundingClientRect().width + 14 : 220;
        list.scrollBy({ left: step * direction * 2, behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      if (prev) prev.addEventListener('click', function () { scrollBy(-1); });
      if (next) next.addEventListener('click', function () { scrollBy(1); });
    });
  }

  /* ----------------------------------------------------------------------
     Classroom reel lightbox.
     Clicking a tile opens the clip full size with sound, the creator's handle
     linked underneath, and arrows through the rest of the row.

     Built on <dialog>.showModal(), so focus trapping, Escape and making the
     rest of the page inert come from the browser. Where <dialog> is missing the
     tiles are left exactly as the server rendered them: an <a> to the creator's
     profile, which is a reasonable place to end up.
     ---------------------------------------------------------------------- */
  function initReelLightbox() {
    document.querySelectorAll('[data-mts-reel-dialog]').forEach(function (dialog) {
      var section = dialog.closest('.mts-classrooms');
      if (!section || typeof dialog.showModal !== 'function') return;

      var triggers = Array.prototype.slice.call(section.querySelectorAll('[data-mts-reel-open]'));
      if (!triggers.length) return;

      var video = dialog.querySelector('[data-mts-lb-video]');
      var caption = dialog.querySelector('[data-mts-lb-caption]');
      var closeBtn = dialog.querySelector('[data-mts-lb-close]');
      var prevBtn = dialog.querySelector('[data-mts-lb-prev]');
      var nextBtn = dialog.querySelector('[data-mts-lb-next]');
      if (!video) return;

      var scroller = document.querySelector('.page-wrapper') || document.documentElement;
      var current = 0;
      var opener = null;
      var scrollLock = '';

      var single = triggers.length < 2;
      if (single) {
        if (prevBtn) prevBtn.hidden = true;
        if (nextBtn) nextBtn.hidden = true;
      }

      function setCaption(handle, profile) {
        caption.textContent = '';
        if (!handle) return;
        var el;
        if (profile) {
          el = document.createElement('a');
          el.href = profile;
          el.target = '_blank';
          el.rel = 'noopener noreferrer';
        } else {
          el = document.createElement('span');
        }
        el.textContent = handle;
        caption.appendChild(el);
      }

      function show(index) {
        current = (index + triggers.length) % triggers.length;
        var trigger = triggers[current];
        var src = trigger.getAttribute('data-video') || '';
        var poster = trigger.getAttribute('data-poster') || '';

        video.pause();
        if (poster) video.setAttribute('poster', poster);
        else video.removeAttribute('poster');

        /* Shopify knows the clip's real shape, so use it rather than assuming 9:16. Hard-coding
           the portrait ratio letterboxes anything that was not shot that way, and the height
           rule in the CSS then sizes the width from whatever we set here. */
        video.style.aspectRatio = trigger.getAttribute('data-ratio') || '9 / 16';
        if (video.getAttribute('src') !== src) video.setAttribute('src', src);
        video.currentTime = 0;
        video.muted = false;

        setCaption(trigger.getAttribute('data-handle'), trigger.getAttribute('data-profile'));

        /* Called straight out of a click or a key press, so this counts as a user gesture and
           the sound is allowed. If a browser refuses anyway - some do on a first visit, whatever
           the gesture - fall back to playing muted rather than showing a still frame and hoping
           somebody finds the play button. The controls are there to unmute. */
        var playing = video.play();
        if (playing && playing.catch) {
          playing.catch(function () {
            video.muted = true;
            var retry = video.play();
            if (retry && retry.catch) retry.catch(function () {});
          });
        }
      }

      function open(index, trigger) {
        opener = trigger;

        /* The tile underneath is looping its own muted preview. Stop it, or two copies of the
           same clip run at once behind the backdrop. */
        section.querySelectorAll('[data-mts-hover-video]').forEach(function (preview) {
          preview.pause();
          preview.currentTime = 0;
        });

        scrollLock = scroller.style.overflow;
        scroller.style.overflow = 'hidden';
        dialog.showModal();
        show(index);
      }

      function close() {
        video.pause();
        /* Drop the source as well as pausing: a paused video with a src can keep buffering. */
        video.removeAttribute('src');
        video.load();
        scroller.style.overflow = scrollLock;
        if (opener && typeof opener.focus === 'function') opener.focus();
        opener = null;
      }

      triggers.forEach(function (trigger, index) {
        trigger.addEventListener('click', function (event) {
          if (!trigger.getAttribute('data-video')) return;
          event.preventDefault();
          open(index, trigger);
        });
      });

      if (closeBtn) closeBtn.addEventListener('click', function () { dialog.close(); });
      if (prevBtn) prevBtn.addEventListener('click', function () { show(current - 1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { show(current + 1); });

      /* Clicking the backdrop closes. With showModal() the backdrop's clicks report the dialog
         itself as the target, so anything landing on a child is a click on the player. */
      dialog.addEventListener('click', function (event) {
        if (event.target === dialog) dialog.close();
      });

      dialog.addEventListener('keydown', function (event) {
        if (single) return;
        if (event.key === 'ArrowLeft') { event.preventDefault(); show(current - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); show(current + 1); }
      });

      /* Swipe and scroll, so it moves the way a feed moves rather than only by the arrows.
         Either axis works: sideways matches the arrows, up and down matches what a phone
         already trains people to do on a stack of short videos. */
      var SWIPE = 44;
      var startX = 0;
      var startY = 0;
      var swiping = false;

      dialog.addEventListener('touchstart', function (event) {
        if (single || event.touches.length !== 1) return;
        var touch = event.touches[0];

        /* The video carries the browser's own controls along its bottom edge. A drag that
           starts there is somebody scrubbing, so leave it alone. */
        var box = video.getBoundingClientRect();
        if (touch.clientY > box.bottom - 56 && touch.clientY <= box.bottom &&
            touch.clientX >= box.left && touch.clientX <= box.right) return;

        startX = touch.clientX;
        startY = touch.clientY;
        swiping = true;
      }, { passive: true });

      dialog.addEventListener('touchend', function (event) {
        if (!swiping) return;
        swiping = false;
        var touch = event.changedTouches[0];
        if (!touch) return;
        var dx = touch.clientX - startX;
        var dy = touch.clientY - startY;
        var horizontal = Math.abs(dx) > Math.abs(dy);
        var travel = horizontal ? dx : dy;
        if (Math.abs(travel) < SWIPE) return;
        show(travel < 0 ? current + 1 : current - 1);
      }, { passive: true });

      /* A trackpad fires one wheel event per pixel of movement, so without a cooldown a single
         two-finger flick would run through the whole row. */
      var wheelLock = 0;
      dialog.addEventListener('wheel', function (event) {
        if (single) return;
        var now = Date.now();
        if (now - wheelLock < 500) return;
        var delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
        if (Math.abs(delta) < 24) return;
        wheelLock = now;
        event.preventDefault();
        show(delta > 0 ? current + 1 : current - 1);
      }, { passive: false });

      /* Fires for the close button, the backdrop and Escape alike, so the teardown lives here
         once rather than on each of the three. */
      dialog.addEventListener('close', close);
    });
  }

  /* ----------------------------------------------------------------------
     Personalisation panel.
     mts-product.liquid renders #mts-personalisation as the container the fields
     are meant to live in, but King Product Options ignores it and drops #ymq-box
     in as the form's first child. That leaves the instructions sitting UNDER the
     boxes they describe, which is the wrong way round: you read the field, fill
     it, and only then get told how to fill it.

     So the box is moved into the panel. If the app's placement is ever pointed at
     #mts-personalisation properly this does nothing, because the box is already
     where it belongs.
     ---------------------------------------------------------------------- */
  function initPersonalisation() {
    var panel = document.querySelector('[data-mts-personalisation]');
    if (!panel) return;

    function home() {
      var box = document.getElementById('ymq-box');
      if (!box || panel.contains(box)) return;
      /* Name stamps with the native live preview (snippets/mts-stamp-preview) have their own
         name field. Until the King option set is unassigned from those products in the app,
         its box would arrive as a second name field, so it is dropped here. */
      if (panel.hasAttribute('data-mts-stamp')) { box.remove(); return; }
      panel.appendChild(box);
      panel.classList.add('mts-pdp__pers--filled');
    }

    /* King draws its field titles as a <span class="ymq_lable">, not a <label>, so the inputs
       reach the browser with no accessible name at all - "Your Name" and "Bottom Line Message"
       are announced as an unlabelled edit box. These are the two fields that carry every
       personalised order on the site, and they were the only unlabelled inputs on it.

       Wired with aria-labelledby rather than by rewriting their markup into real <label>s: the
       app re-renders this box on every variant change and matches its own elements by class and
       tag, so changing the structure would either be undone or break its validation. Attributes
       it does not read are safe. The help text becomes the description, and a required field
       says so.

       Attribute writes do not trip the childList observer below, so this cannot loop. */
    function label() {
      panel.querySelectorAll('.ymq-options-box').forEach(function (box, i) {
        var key = box.id || 'pers-' + i;
        var name = box.querySelector('.ymq_lable');
        var help = box.querySelector('.ymq_help_box');

        if (name && !name.id) name.id = 'mts-pers-label-' + key;
        if (help && !help.id) help.id = 'mts-pers-help-' + key;

        var required = (box.getAttribute('data-validate') || '').indexOf('required') > -1;

        box.querySelectorAll('input, textarea, select').forEach(function (control) {
          if (control.type === 'hidden') return;
          /* Leave anything the app has already labelled properly alone. */
          if (control.labels && control.labels.length && control.labels[0].textContent.trim()) return;

          if (name && control.getAttribute('aria-labelledby') !== name.id) {
            control.setAttribute('aria-labelledby', name.id);
          }
          if (help && control.getAttribute('aria-describedby') !== help.id) {
            control.setAttribute('aria-describedby', help.id);
          }
          if (required && !control.hasAttribute('aria-required')) {
            control.setAttribute('aria-required', 'true');
          }
        });
      });
    }

    home();
    label();

    /* The app renders after us and re-renders on a variant change, putting the box back at the
       top of the form each time, so this has to keep watching rather than run once. Scoped to
       the buy column - a document-wide subtree observer on a product page is a lot of noise for
       one element. */
    var scope = panel.closest('.mts-pdp__buy') || panel.parentElement;
    if (!scope || typeof MutationObserver !== 'function') return;
    new MutationObserver(function () {
      home();
      label();
    }).observe(scope, { childList: true, subtree: true });
  }

  /* ----------------------------------------------------------------------
     You may also like.
     Shopify only fills recommendations.products when the section is fetched
     through the recommendations endpoint, so the server-rendered grid holds
     fallback cards from the product's own collection and this swaps in the real
     recommendations once they arrive.

     Only swaps if the response actually has cards. A recommendations engine with
     nothing to say would otherwise turn a populated section into an empty one,
     which is worse than the fallback it replaced.
     ---------------------------------------------------------------------- */
  function initRecommendations() {
    document.querySelectorAll('[data-mts-recs]').forEach(function (box) {
      var url = box.getAttribute('data-url');
      var grid = box.querySelector('[data-mts-recs-grid]');
      if (!url || !grid) return;

      fetch(url)
        .then(function (response) {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then(function (html) {
          var parsed = new DOMParser().parseFromString(html, 'text/html');
          var fresh = parsed.querySelector('[data-mts-recs-grid]');
          if (!fresh || !fresh.children.length) return;
          grid.innerHTML = fresh.innerHTML;
        })
        .catch(function () {
          /* Leave the fallback cards alone. */
        });
    });
  }

  /* ----------------------------------------------------------------------
     Compact header on scroll.

     The logo is 298x64 at the top of a desktop page, which is right when you
     arrive and heavy once you are reading. Past the fold it drops to the mobile
     pair Horizon already computes - 205x44 - taking the header from 75px to
     55px. Reusing --header-logo-image-*-mobile rather than picking numbers means
     the aspect ratio is right by construction and the compact size follows the
     mobile logo setting if it is ever changed.

     Two thresholds, not one, and the gap between them matters. Shrinking the
     header removes 20px from the document, and the browser's scroll anchoring
     compensates by pulling scrollTop up by the same 20px - measured, 1200 became
     1180. With a single threshold that lands you back below it, which un-shrinks,
     which pushes you back above it: a header that flickers forever at one scroll
     position. Shrink at 140, restore at 80, and the 60px gap is comfortably wider
     than the 20px the anchor moves.
     ---------------------------------------------------------------------- */
  function initHeaderShrink() {
    if (!document.querySelector('.header-section')) return;

    var wrapper = document.querySelector('.page-wrapper');
    var usesWrapper = !!wrapper &&
      wrapper.scrollHeight > wrapper.clientHeight + 1 &&
      getComputedStyle(wrapper).overflowY !== 'visible';
    var target = usesWrapper ? wrapper : window;

    var SHRINK_AT = 140;
    var RESTORE_AT = 80;
    var compact = false;
    var ticking = false;

    function offset() {
      return usesWrapper ? wrapper.scrollTop : (window.scrollY || document.documentElement.scrollTop || 0);
    }

    function update() {
      ticking = false;
      var y = offset();
      if (!compact && y > SHRINK_AT) {
        compact = true;
        document.body.classList.add('mts-header-compact');
      } else if (compact && y < RESTORE_AT) {
        compact = false;
        document.body.classList.remove('mts-header-compact');
      }
    }

    target.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });

    update();
  }
  /* ----------------------------------------------------------------------
     Pencil divider — BUILDSPEC §6.7.
     The pencil draws a wavy Deep Teal line as the section scrolls through the
     viewport. Its tip tracks the end of the stroke and it stays visible once
     the line is complete. Scroll work is throttled through rAF.
     ---------------------------------------------------------------------- */
  function initPencil() {
    var boxes = document.querySelectorAll('[data-mts-pencil]');
    if (!boxes.length) return;

    boxes.forEach(function (box) {
      var path = box.querySelector('[data-mts-pencil-path]');
      var pencil = box.querySelector('[data-mts-pencil-img]');
      if (!path || !pencil) return;

      var length = path.getTotalLength();

      /* mts-pencil-teal.webp is 1600x400 with 179px of transparent padding on every side, so the
         drawn tip is nowhere near the image's edge — measured off the alpha channel it sits at
         88.75% across and 51.13% down. transform-origin in mts-custom.css is set to exactly that
         point, which makes the tip the one place the 45° rotation leaves alone; landing it on the
         stroke is then just a matter of subtracting the same fractions of the rendered box.

         Assuming the tip was at the image's right edge is what produced the visible gap: it put
         the transparent corner on the line and left the actual pencil about 23px up and to the
         left of it. These two fractions and the transform-origin must always agree. */
      var TIP_X = 0.8875;
      var TIP_Y = 0.5113;

      function place(drawn) {
        var point = path.getPointAtLength(drawn);
        var scaleX = (box.clientWidth - 48) / 1200;
        pencil.style.left = 24 + point.x * scaleX - pencil.clientWidth * TIP_X + 'px';
        pencil.style.top = 26 + point.y * (110 / 120) - pencil.clientHeight * TIP_Y + 'px';
      }

      // Reduced motion removes the movement, not the artwork. BUILDSPEC §6.7 wants the pencil
      // visible at the end of the stroke, so the finished state is rendered directly: full line,
      // pencil parked at its end. Hiding it entirely would drop a design element rather than an
      // animation.
      if (reduceMotion) {
        if (pencil.complete) place(length);
        else pencil.addEventListener('load', function () { place(length); });
        return;
      }

      path.style.strokeDasharray = length;
      path.style.strokeDashoffset = length;

      var ticking = false;

      function update() {
        ticking = false;
        var rect = box.getBoundingClientRect();
        var progress = (window.innerHeight - rect.top - 60) / (window.innerHeight * 0.7);
        progress = Math.max(0, Math.min(1, progress));

        var drawn = Math.max(length * progress, 1);
        path.style.strokeDashoffset = length - drawn;
        place(drawn);
      }

      function onScroll() {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      }

      /* Horizon does not scroll the window. `html` and `body` are locked to the viewport height
         and `.page-wrapper` is the element with `overflow: auto`, so it is the real scroller.
         Scroll events do not bubble, which means a listener on `window` never fires and anything
         driven by it silently does nothing.

         Listening on `document` in the capture phase catches scroll from any element, including
         that wrapper, and still works if the window ever becomes the scroller. Both are attached
         so this holds whichever way Horizon is configured. */
      document.addEventListener('scroll', onScroll, { passive: true, capture: true });
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      update();
    });
  }

  /* ----------------------------------------------------------------------
     Product gallery — swap the main image when a thumbnail is chosen.
     Purely an enhancement: without JS every thumbnail is still a real image
     and the main image is the featured one, so nothing is lost.
     ---------------------------------------------------------------------- */
  function bindGallery(gallery) {
    var main = gallery.querySelector('[data-mts-gallery-main]');
    if (!main) return;

    gallery.querySelectorAll('[data-mts-gallery-thumb]').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var full = thumb.getAttribute('data-full');
        if (!full) return;
        main.removeAttribute('srcset');
        main.src = full;
        gallery.querySelectorAll('[data-mts-gallery-thumb]').forEach(function (t) {
          t.classList.toggle('is-active', t === thumb);
        });
      });
    });
  }

  function initGallery() {
    document.querySelectorAll('[data-mts-gallery]').forEach(bindGallery);
  }

  /* ----------------------------------------------------------------------
     Variant choice without a page load - ink colour, and gift card amounts.

     The swatches are server-rendered as links to ?variant=… so the URL is always
     right and the form cannot desync from the variant. Left as plain links they
     also reload the page, which measured 740ms, threw the visitor back to the
     top, and - the real cost - emptied anything already typed into King's
     personalisation fields.

     So the links are upgraded here rather than replaced. The section is
     re-rendered server-side for the chosen variant via ?section_id=, and only
     the pieces that differ are swapped in. The personalisation container is
     deliberately not one of them: it is never touched, so a half-filled name or
     an uploaded file survives a change of colour.

     Delegated, so swatches swapped in by this function keep working. On any
     failure it falls through to the ordinary navigation, which is the behaviour
     it is replacing.
     ---------------------------------------------------------------------- */
  function initVariantSwap() {
    var pdp = document.querySelector('[data-mts-pdp]');
    if (!pdp || !window.fetch || !window.history || !window.DOMParser) return;

    var sectionId = pdp.getAttribute('data-mts-pdp');

    /* Everything that differs between one variant and another, on both the stamp page and the
       gift card page. Selectors that are not on the current page simply miss.

       What is NOT here matters more than what is. The stamp page's personalisation container and
       the gift card's recipient fields are both left alone, so a half-typed name, an uploaded
       file or a recipient's email address survives a change of variant. Replacing the whole
       section would take all of it with them, which is the bug this exists to fix. */
    var SWAP = [
      '.mts-pdp__stage',
      '.mts-pdp__thumbs',
      '.mts-pdp__price',
      '.mts-pdp__optlabel',
      '.mts-pdp__swatches',
      '.mts-pdp__optlabel--icon',
      '.mts-pdp__swatches--icons',
      '.mts-pdp__atc',
      '.mts-gc__media',
      '.mts-gc__price',
      '.mts-gc__optlabel',
      '.mts-gc__amounts',
      '.mts-gc__atc'
    ];

    var busy = false;

    pdp.addEventListener('click', function (event) {
      var swatch = event.target.closest('.mts-pdp__sw[href], .mts-gc__amount[href]');
      if (!swatch || !pdp.contains(swatch)) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

      event.preventDefault();
      if (busy) return;
      busy = true;

      var href = swatch.getAttribute('href');
      var url = href + (href.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + sectionId;
      var media = pdp.querySelector('[data-mts-gallery], .mts-gc__media');
      if (media) media.classList.add('is-swapping');

      fetch(url)
        .then(function (response) {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then(function (html) {
          var fresh = new DOMParser().parseFromString(html, 'text/html');

          SWAP.forEach(function (selector) {
            var from = fresh.querySelector(selector);
            var to = pdp.querySelector(selector);
            if (from && to) to.replaceWith(from.cloneNode(true));
          });

          /* The hidden field is inside the form King has injected into, so its value is set
             rather than the element replaced - replacing it risks taking their fields with it. */
          var freshId = fresh.querySelector('input[name="id"]');
          var liveId = pdp.querySelector('input[name="id"]');
          if (freshId && liveId) liveId.value = freshId.value;

          var newMedia = pdp.querySelector('[data-mts-gallery], .mts-gc__media');
          if (newMedia) {
            if (newMedia.hasAttribute('data-mts-gallery')) bindGallery(newMedia);
            newMedia.classList.remove('is-swapping');
          }

          /* replaceState, not pushState: Back should leave the product, not walk the visitor
             back through every colour they looked at. */
          history.replaceState({}, '', href);
          busy = false;

          /* Tell the live stamp preview which ink (or, on the fabric stamp, which icon) was
             chosen. The swatch carries its hex in data-ink, an icon tile its key in
             data-icon (sections/mts-product.liquid). The freshly fetched section goes along
             too, so the preview can lift the new icon's artwork from it. */
          if (typeof CustomEvent === 'function') {
            document.dispatchEvent(new CustomEvent('mts:variant', {
              detail: { ink: swatch.getAttribute('data-ink'), icon: swatch.getAttribute('data-icon'), href: href, fresh: fresh }
            }));
          }
        })
        .catch(function () {
          /* Whatever went wrong, the link still works. */
          window.location = href;
        });
    });
  }

  /* ----------------------------------------------------------------------
     Show more — collection and search grids.

     Paging through 83 products four pages at a time made the visitor lose their
     place on every click. The button appends the next page in place instead.

     Progressive enhancement, deliberately: the server still renders real
     Previous/Next links, so the grid pages normally with JavaScript off and for
     crawlers. Only once this runs are those links hidden and replaced with the
     button, so there is never a moment where paging is impossible.

     A "load more" button rather than infinite scroll: the footer stays
     reachable, the back button keeps working, and nothing loads that the
     visitor did not ask for.
     ---------------------------------------------------------------------- */
  function initLoadMore() {
    var navs = document.querySelectorAll('[data-mts-more]');
    if (!navs.length) return;

    navs.forEach(function (nav) {
      var targetSelector = nav.getAttribute('data-mts-more-target');
      var grid = targetSelector ? document.querySelector(targetSelector) : null;
      if (!grid) return;

      function nextUrl() {
        var link = nav.querySelector('a[rel="next"]');
        return link ? link.getAttribute('href') : null;
      }

      if (!nextUrl()) return;

      var status = document.createElement('p');
      status.className = 'visually-hidden';
      status.setAttribute('role', 'status');

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'mts-btn mts-btn--raspberry mts-more';
      button.textContent = 'Show more';

      function finish() {
        if (nextUrl()) {
          button.disabled = false;
          button.textContent = 'Show more';
          return;
        }
        button.remove();
        nav.remove();
      }

      button.addEventListener('click', function () {
        var url = nextUrl();
        if (!url) return;

        button.disabled = true;
        button.textContent = 'Loading…';

        fetch(url, { credentials: 'same-origin' })
          .then(function (response) {
            if (!response.ok) throw new Error('HTTP ' + response.status);
            return response.text();
          })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var incoming = doc.querySelector(targetSelector);
            var added = 0;

            if (incoming) {
              Array.prototype.slice.call(incoming.children).forEach(function (child) {
                grid.appendChild(document.importNode(child, true));
                added += 1;
              });
            }

            /* Swap in the fetched page's own pagination so the next URL advances. */
            var incomingNav = doc.querySelector('[data-mts-more]');
            nav.innerHTML = incomingNav ? incomingNav.innerHTML : '';

            status.textContent = added + (added === 1 ? ' more product loaded' : ' more products loaded');
            finish();
          })
          .catch(function () {
            /* Leave the visitor a way forward rather than a dead button. */
            button.disabled = false;
            button.textContent = 'Show more';
            status.textContent = 'Could not load more. Please try again.';
            nav.classList.remove('is-enhanced');
          });
      });

      nav.classList.add('is-enhanced');
      nav.parentNode.insertBefore(status, nav);
      nav.parentNode.insertBefore(button, nav);
    });
  }

  /* Trust banner — one fact at a time below 901px.

     Stacked, the band is four centred lines of different lengths: the icons stagger across it
     and it stands 182px tall to carry four short facts. Rotated, it holds at one line and each
     fact gets the middle of the screen.

     2.6s a fact. Long enough to read seven words at a glance, short enough that someone
     scrolling at a normal pace sees two or three before the band leaves the screen.

     The rotation only runs while the band is actually on screen, and it restarts from the
     first fact each time it comes back into view — so the fact Liam most wants read is the one
     every visitor sees, rather than whichever the timer happened to be on.

     Tapping the band pauses it. That is the pause mechanism WCAG 2.2.2 asks for on content
     that moves for more than five seconds, though an undiscoverable one; a visible control is
     the fuller answer if it is ever wanted. Reduced motion skips the whole thing and leaves
     the stacked list, which is also what a visitor without JavaScript gets. */
  function initTrustRotator() {
    var root = document.querySelector('.mts-trust');
    if (!root || reduceMotion) return;

    var items = [].slice.call(root.querySelectorAll('.mts-trust__item'));
    if (items.length < 2) return;

    var narrow = window.matchMedia('(max-width: 900px)');
    var HOLD = 2600;
    var timer = null;
    var index = 0;
    var paused = false;
    var onScreen = true;

    function paint() {
      items.forEach(function (item, i) {
        item.classList.toggle('is-current', i === index);
      });
    }

    /* document.hidden is read here rather than tracked through a visibilitychange listener:
       latching it into a flag means a tab switch can turn the rotation off and never turn it
       back on. Read at tick time it cannot get stuck. */
    function tick() {
      if (paused || !onScreen || document.hidden) return;
      index = (index + 1) % items.length;
      paint();
    }

    function start() {
      root.classList.add('mts-trust--rotating');
      paint();
      if (!timer) timer = window.setInterval(tick, HOLD);
    }

    function stop() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
      root.classList.remove('mts-trust--rotating');
      items.forEach(function (item) {
        item.classList.remove('is-current');
      });
    }

    function sync() {
      if (narrow.matches) start();
      else stop();
    }

    root.addEventListener('pointerdown', function () {
      paused = !paused;
    });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            onScreen = entry.isIntersecting;
            if (entry.isIntersecting && narrow.matches) {
              index = 0;
              paused = false;
              paint();
            }
          });
        },
        { threshold: 0.4 }
      ).observe(root);
    }

    if (narrow.addEventListener) narrow.addEventListener('change', sync);
    else narrow.addListener(sync);

    sync();
  }

  function init() {
    initReveal();
    initReels();
    initTrustRotator();
    initReelLightbox();
    initPersonalisation();
    initRecommendations();
    initVariantSwap();
    initHeaderShrink();
    initPencil();
    initGallery();
    initLoadMore();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
